import { Alert, Button, Card, DatePicker, Form, Input, Select, Space, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useEffect } from 'react';
import dayjs, { Dayjs } from 'dayjs';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';
import { EducationStudentSelect } from '@/pages/education/components/EducationLookupSelects';

type ActivityRow = { id: string; code: string; name: string };
type Achievement = {
  id: string;
  activity_id: string;
  student_id: string;
  title: string;
  achieved_on: string;
  level: string;
  notes?: string | null;
  evidence_url?: string | null;
};
type FormValues = {
  activity_id: string;
  student_id: string;
  title: string;
  achieved_on: Dayjs;
  level: string;
  notes?: string;
  evidence_url?: string;
};

const levels = ['school', 'district', 'state', 'national', 'other'];

export default function ActivityAchievementFormPage() {
  const { t } = useTranslation('education');
  const navigate = useNavigate();
  const { id } = useParams();
  const [searchParams] = useSearchParams();
  const isNew = !id;
  const queryClient = useQueryClient();
  const [form] = Form.useForm<FormValues>();
  const user = useAuthStore((s) => s.user);
  const canManage = Boolean(
    user?.permissions?.includes('education.activity.manage') ||
      user?.permissions?.includes('education.admin')
  );

  const activities = useQuery({
    queryKey: ['education-activities-for-achievements'],
    queryFn: async () => {
      const res = await api.get<ActivityRow[]>('/education/activities');
      return Array.isArray(res.data) ? res.data : [];
    },
  });

  const achievement = useQuery({
    queryKey: ['education-activity-achievement', id],
    enabled: Boolean(id),
    queryFn: async () => (await api.get<Achievement>(`/education/activity-achievements/${id}`)).data,
  });

  useEffect(() => {
    if (achievement.data) {
      form.setFieldsValue({
        activity_id: achievement.data.activity_id,
        student_id: achievement.data.student_id,
        title: achievement.data.title,
        achieved_on: dayjs(achievement.data.achieved_on),
        level: achievement.data.level,
        notes: achievement.data.notes || undefined,
        evidence_url: achievement.data.evidence_url || undefined,
      });
    } else if (isNew) {
      form.setFieldsValue({
        activity_id: searchParams.get('activity_id') || undefined,
        student_id: searchParams.get('student_id') || undefined,
        achieved_on: dayjs(),
        level: 'school',
      });
    }
  }, [achievement.data, form, isNew, searchParams]);

  const save = useMutation({
    mutationFn: (values: FormValues) => {
      const payload = {
        ...values,
        achieved_on: values.achieved_on.format('YYYY-MM-DD'),
        evidence_url: values.evidence_url?.trim() || null,
        notes: values.notes?.trim() || null,
      };
      return isNew
        ? api.post('/education/activity-achievements', payload)
        : api.put(`/education/activity-achievements/${id}`, payload);
    },
    onSuccess: () => {
      message.success(t(isNew ? 'activityAchievementCreated' : 'activityAchievementUpdated'));
      queryClient.invalidateQueries({ queryKey: ['education-activity-achievements'] });
      navigate('/education/activities/achievements');
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  if (isNew && !canManage) {
    return (
      <EducationPageChrome title={t('newActivityAchievement')}>
        <Alert type="warning" showIcon message={t('unavailable', { status: 403 })} />
      </EducationPageChrome>
    );
  }

  return (
    <EducationPageChrome
      title={t(isNew ? 'newActivityAchievement' : 'editActivityAchievement')}
      description={t('activityAchievementFormIntro')}
      extra={
        <Button onClick={() => navigate('/education/activities/achievements')}>
          {t('backToActivityAchievements')}
        </Button>
      }
    >
      <Card size="small" style={{ maxWidth: 760 }} loading={!isNew && achievement.isLoading}>
        <Form form={form} layout="vertical" onFinish={(values) => save.mutate(values)} disabled={!canManage}>
          <Form.Item name="activity_id" label={t('activity')} rules={[{ required: true }]}>
            <Select
              showSearch
              optionFilterProp="label"
              loading={activities.isLoading}
              options={(activities.data || []).map((row) => ({
                value: row.id,
                label: `${row.code} — ${row.name}`,
              }))}
            />
          </Form.Item>
          <Form.Item name="student_id" label={t('student')} rules={[{ required: true }]}>
            <EducationStudentSelect />
          </Form.Item>
          <Form.Item name="title" label={t('achievementTitle')} rules={[{ required: true, max: 250 }]}>
            <Input maxLength={250} />
          </Form.Item>
          <Space size="large" align="start" wrap>
            <Form.Item name="achieved_on" label={t('achievedOn')} rules={[{ required: true }]}>
              <DatePicker />
            </Form.Item>
            <Form.Item name="level" label={t('achievementLevel')} rules={[{ required: true }]}>
              <Select
                style={{ width: 180 }}
                options={levels.map((value) => ({ value, label: t(`achievementLevel_${value}`) }))}
              />
            </Form.Item>
          </Space>
          <Form.Item name="notes" label={t('notes')}>
            <Input.TextArea rows={4} maxLength={2000} showCount />
          </Form.Item>
          <Form.Item
            name="evidence_url"
            label={t('evidenceUrl')}
            extra={t('activityAchievementEvidenceHint')}
            rules={[{ type: 'url', message: t('validUrlRequired') }]}
          >
            <Input maxLength={1000} placeholder="https://…" />
          </Form.Item>
          {canManage ? (
            <Space>
              <Button type="primary" htmlType="submit" loading={save.isPending}>
                {t('save')}
              </Button>
              <Button onClick={() => navigate('/education/activities/achievements')}>{t('cancel')}</Button>
            </Space>
          ) : null}
        </Form>
      </Card>
    </EducationPageChrome>
  );
}
